import React, { useEffect, useState } from 'react';
import { Spin, Flex, Card, Typography, Tag, Empty } from 'antd';
import { useHistory } from 'react-router-dom';
import { mdiAccount, mdiEmail, mdiMapMarker, mdiBriefcase, mdiChartLine, mdiRoutes } from '@mdi/js';
import Icon from '@mdi/react';

import JourneyIdsFormatter from 'modules/ReportModule/formatters/JourneyIdsFormatter';
import AutomaticDrawer from 'components/Drawers/AutomaticDrawer';
import useQueryParams from 'hooks/useQueryParams';
import ninjaApi from 'store/redux/apis';
import P from 'router/paths';

import { CountryFormatter } from 'modules/CRMModule/formatters';
import { DateFormatter, IntegrationFormatter } from 'components/tables/NinjaTable/formatters';
import CrmInfoRow from '../components/CrmInfoRow';
import CrmInfoSection from '../components/CrmInfoSection';
import CrmFilterableField from '../components/CrmFilterableField';
import CrmPersonalInformation from '../components/CrmPersonalInformation';

const { Title, Text } = Typography;

const ContactInfoDrawer = (props) => {
  const { searchParams, removeParams, setUrlParams } = useQueryParams();
  const { contact_id } = searchParams;
  const history = useHistory();

  const [fetchContact, { isLoading }] = ninjaApi.useLazyGetSingleContactQuery();
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    if (contact_id) {
      fetchContact({ id: contact_id })
        .unwrap()
        .then(setContactData)
        .catch(() => setContactData(null));
    }
  }, [contact_id]);

  const filterContact = (key, value) => {
    history.push(P.CRM.CONTACTS);
    removeParams(['contact_id', 'date_from', 'date_to']);
    setUrlParams({ [key]: value });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify='center' align='center' style={{ minHeight: 400 }}>
          <Spin size='large' tip='Loading contact information...' />
        </Flex>
      );
    }

    if (!contactData) {
      return <Empty description='Contact not found' style={{ marginTop: 100 }} />;
    }

    const fullName =
      [contactData.first_name, contactData.middle_name, contactData.last_name]
        .filter(Boolean)
        .join(' ') || 'No Name';

    return (
      <div style={{ padding: '8px 0' }}>
        <Card className='bg-orangeGradient mb-6 text-white'>
          <Flex justify='space-between' align='center'>
            <Flex vertical gap='small'>
              <Title level={3} style={{ color: 'white', margin: 0 }}>
                {fullName}
              </Title>
              <Flex size='middle' className='w-full'>
                <Tag bordered={false} className='w-fit flex items-center'>
                  <Text className='text-xs font-medium text-color-medium mr-1'>Contact ID:</Text>
                  <CrmFilterableField
                    value={contactData.id}
                    filterKey='id'
                    onFilter={filterContact}
                  />
                </Tag>
                {contactData.deals_count && (
                  <Tag color='green' className='flex items-center font-medium'>
                    <Icon path={mdiChartLine} size={0.5} style={{ marginRight: 4 }} />
                    <span> {contactData.deals_count !== 1 ? 's' : ''}</span>
                  </Tag>
                )}
              </Flex>
            </Flex>
            <div>
              <IntegrationFormatter
                className='!w-12 !h-12'
                row={{ integration: { name: contactData.source, type: contactData.source } }}
              />
            </div>
          </Flex>
        </Card>

        <CrmPersonalInformation data={contactData} />

        <CrmInfoSection title='Contact Information' icon={mdiEmail}>
          <CrmInfoRow label='Email'>
            <CrmFilterableField
              value={contactData.email}
              filterKey='email'
              onFilter={filterContact}
            />
          </CrmInfoRow>
          <CrmInfoRow label='Phone'>
            <CrmFilterableField
              value={contactData.phone}
              filterKey='phone'
              onFilter={filterContact}
            />
          </CrmInfoRow>
          <CrmInfoRow label='Emergency Phone'>
            <Text className='text-sm'>{contactData.emergency_phone || '-'}</Text>
          </CrmInfoRow>
        </CrmInfoSection>

        <CrmInfoSection title='Company Information' icon={mdiBriefcase}>
          <CrmInfoRow label='Company Name'>
            <Text strong>{contactData.company_name || '-'}</Text>
          </CrmInfoRow>
          <CrmInfoRow label='Job Title'>
            <Text className='text-sm'>{contactData.job_title || '-'}</Text>
          </CrmInfoRow>
          <CrmInfoRow label='Company Email'>
            <Text className='text-sm'>{contactData.company_email || '-'}</Text>
          </CrmInfoRow>
        </CrmInfoSection>

        <CrmInfoSection title='Activity & Timeline' icon={mdiRoutes}>
          <CrmInfoRow label='Journey' span={24}>
            {contactData.session_id !== null ? (
              <JourneyIdsFormatter row={contactData} />
            ) : (
              <Text type='secondary'>No journey data</Text>
            )}
          </CrmInfoRow>
          <CrmInfoRow label='Synced At'>
            {contactData.created_at ? (
              <DateFormatter defaultValue={contactData.created_at} />
            ) : (
              <Text type='secondary'>-</Text>
            )}
          </CrmInfoRow>
          <CrmInfoRow label='Registered At'>
            {contactData.registered_at ? (
              <DateFormatter defaultValue={contactData.registered_at} />
            ) : (
              <Text type='secondary'>-</Text>
            )}
          </CrmInfoRow>
        </CrmInfoSection>
      </div>
    );
  };

  return (
    <AutomaticDrawer
      size='large'
      title={
        <Flex align='center' gap='small'>
          <span>Contact Details</span>
        </Flex>
      }
      {...props}
    >
      {renderContent()}
    </AutomaticDrawer>
  );
};

ContactInfoDrawer.requiredParams = {
  contact_id: (p) => parseInt(p) > 0,
};

export default ContactInfoDrawer;
